// ./src/components/generic/AppToolbar/AppToolbar.tsx
import React from "react";
import {
  Group,
  Paper,
  Stack,
  Text,
  Divider,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

export interface AppToolbarSection {
  /** Unique identifier for the section */
  id: string;

  /** Items to display in this section */
  items: React.ReactNode[];

  /** Label for the section (optional) */
  label?: string;

  /** Alignment of items within the section */
  align?: "left" | "center" | "right";

  /** Whether to wrap items to new lines */
  wrap?: "wrap" | "nowrap" | "wrap-reverse";

  /** Gap between items in this section */
  gap?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Hide this section on mobile */
  hideOnMobile?: boolean;
}

export interface AppToolbarProps {
  /** Sections to display in the toolbar */
  sections: AppToolbarSection[];

  /** Layout direction */
  direction?: "horizontal" | "vertical";

  /** Overall alignment of sections */
  justify?:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";

  /** Whether to show as elevated paper */
  withBorder?: boolean;

  /** Whether to show shadows */
  withShadow?: boolean;

  /** Padding inside the toolbar */
  padding?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Gap between sections */
  sectionGap?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show dividers between sections */
  withDividers?: boolean;

  /** Size variant */
  size?: "sm" | "md" | "lg";

  /** Additional className for styling */
  className?: string;

  /** Additional styles */
  style?: React.CSSProperties;

  /** Quick setup for common patterns */
  preset?:
    | "page-header"
    | "table-toolbar"
    | "modal-toolbar"
    | "sidebar-toolbar"
    | "custom";

  /** Enable responsive behavior (stack vertically on mobile) */
  responsive?: boolean;

  /** Custom breakpoint for mobile behavior (default: 'sm' = 768px) */
  mobileBreakpoint?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether sections should wrap on mobile */
  wrapOnMobile?: boolean;
}

export const AppToolbar: React.FC<AppToolbarProps> = ({
  sections,
  direction = "horizontal",
  justify = "space-between",
  withBorder = false,
  withShadow = false,
  padding = "md",
  sectionGap = "md",
  withDividers = false,
  className,
  style,
  preset = "custom",
  responsive = true,
  mobileBreakpoint = "sm",
  wrapOnMobile = true,
}) => {
  const theme = useMantineTheme();

  // Check if we're on mobile
  const isMobile = useMediaQuery(
    `(max-width: ${theme.breakpoints[mobileBreakpoint]})`
  );

  // Apply preset configurations
  const getPresetConfig = () => {
    switch (preset) {
      case "page-header":
        return {
          withBorder: true,
          withShadow: false,
          padding: "lg" as const,
          justify: "space-between" as const,
          sectionGap: "lg" as const,
          responsive: true,
        };

      case "table-toolbar":
        return {
          withBorder: true,
          withShadow: false,
          padding: "sm" as const,
          justify: "space-between" as const,
          sectionGap: "md" as const,
          responsive: true,
          wrapOnMobile: true,
        };

      case "modal-toolbar":
        return {
          withBorder: false,
          withShadow: false,
          padding: "md" as const,
          justify: "flex-end" as const,
          sectionGap: "sm" as const,
          responsive: true,
        };

      case "sidebar-toolbar":
        return {
          direction: "vertical" as const,
          withBorder: false,
          withShadow: false,
          padding: "sm" as const,
          sectionGap: "sm" as const,
          responsive: false,
        };

      default:
        return {};
    }
  };

  const presetConfig = getPresetConfig();

  // Merge preset config with explicit props (explicit props take precedence)
  const finalConfig = {
    direction: direction,
    justify: justify,
    withBorder: withBorder,
    withShadow: withShadow,
    padding: padding,
    sectionGap: sectionGap,
    responsive: responsive,
    wrapOnMobile: wrapOnMobile,
    ...presetConfig,
  };

  // Responsive adjustments
  const effectiveDirection =
    finalConfig.responsive && isMobile && finalConfig.direction === "horizontal"
      ? "vertical"
      : finalConfig.direction;

  const effectivePadding =
    isMobile && finalConfig.responsive
      ? finalConfig.padding === "xl" || finalConfig.padding === "lg"
        ? "sm"
        : "xs"
      : finalConfig.padding;

  const effectiveSectionGap =
    isMobile && finalConfig.responsive
      ? finalConfig.sectionGap === "xl" || finalConfig.sectionGap === "lg"
        ? "sm"
        : "xs"
      : finalConfig.sectionGap;

  const effectiveWrap =
    isMobile && finalConfig.wrapOnMobile ? "wrap" : "nowrap";

  // Filter out hidden sections on mobile
  const visibleSections = isMobile
    ? sections.filter((section) => !section.hideOnMobile)
    : sections;

  const renderSection = (section: AppToolbarSection, isLast: boolean) => {
    const sectionAlign = section.align || "left";
    const sectionJustify =
      sectionAlign === "left"
        ? "flex-start"
        : sectionAlign === "center"
        ? "center"
        : "flex-end";

    // Adjust section gap for mobile
    const sectionItemGap =
      isMobile && finalConfig.responsive
        ? section.gap === "lg" || section.gap === "xl"
          ? "xs"
          : "xs"
        : section.gap || "sm";

    const sectionContent = (
      <div
        key={section.id}
        style={{ width: effectiveDirection === "vertical" ? "100%" : "auto" }}
      >
        {section.label && (
          <Text size="sm" fw={500} mb="xs" c="dimmed">
            {section.label}
          </Text>
        )}

        {effectiveDirection === "horizontal" ? (
          <Group
            gap={sectionItemGap}
            justify={sectionJustify}
            wrap={section.wrap || effectiveWrap}
          >
            {section.items}
          </Group>
        ) : (
          <Stack gap={sectionItemGap} align={sectionAlign}>
            {section.items}
          </Stack>
        )}
      </div>
    );

    // Only show dividers on desktop or if explicitly enabled
    const showDivider =
      withDividers && !isLast && (!isMobile || !finalConfig.responsive);

    if (showDivider && effectiveDirection === "horizontal") {
      return (
        <React.Fragment key={section.id}>
          {sectionContent}
          <Divider orientation="vertical" />
        </React.Fragment>
      );
    }

    if (showDivider && effectiveDirection === "vertical") {
      return (
        <React.Fragment key={section.id}>
          {sectionContent}
          <Divider />
        </React.Fragment>
      );
    }

    return sectionContent;
  };

  const content = (
    <>
      {effectiveDirection === "horizontal" ? (
        <Group
          justify={finalConfig.justify}
          gap={effectiveSectionGap}
          wrap={effectiveWrap}
        >
          {visibleSections.map((section, index) =>
            renderSection(section, index === visibleSections.length - 1)
          )}
        </Group>
      ) : (
        <Stack gap={effectiveSectionGap}>
          {visibleSections.map((section, index) =>
            renderSection(section, index === visibleSections.length - 1)
          )}
        </Stack>
      )}
    </>
  );

  if (finalConfig.withBorder || finalConfig.withShadow) {
    return (
      <Paper
        p={effectivePadding}
        withBorder={finalConfig.withBorder}
        shadow={finalConfig.withShadow ? "sm" : "none"}
        {...(className && { className })}
        style={style}
      >
        {content}
      </Paper>
    );
  }

  return (
    <div
      className={className}
      style={{
        padding: `var(--mantine-spacing-${effectivePadding})`,
        ...style,
      }}
    >
      {content}
    </div>
  );
};
